import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';

function UpdateRehabilitation() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    identifiedNeeds: [],
    interventions: [],
    proposedSteps: [],
    howToImplement: '',
    durationYears: '',
    referrals: [],
    otherReferral: '',
    otherAids: [],
    otherAid: '',
    provincialCouncilAssistance: [],
    yearlyProgress: {},
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message: 'Missing required parameters.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const parseArrayWithOther = (str) => {
    if (!str) return { array: [], otherValue: '' };
    const items = str
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s);
    const otherItem = items.find((item) => item.startsWith('Other:'));
    if (otherItem) {
      const otherValue = otherItem.replace('Other:', '').trim();
      const array = items
        .filter((item) => !item.startsWith('Other:'))
        .concat(['Other']);
      return { array, otherValue };
    }
    return { array: items, otherValue: '' };
  };

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getRehabilitationPlanDetails(
        recordId,
        sheetId
      );

      if (!response.success) {
        setAlert({
          show: true,
          message:
            response.message || 'Failed to load rehabilitation plan details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      const identifiedNeeds = parseArrayWithOther(
        response.record.identifiedNeeds
      );
      const interventions = parseArrayWithOther(response.record.interventions);
      const proposedSteps = parseArrayWithOther(response.record.proposedSteps);
      const referrals = parseArrayWithOther(response.record.referrals);
      const otherAids = parseArrayWithOther(response.record.otherAids);
      const provincialCouncil = parseArrayWithOther(
        response.record.provincialCouncilAssistance
      );

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        identifiedNeeds: identifiedNeeds.array,
        interventions: interventions.array,
        proposedSteps: proposedSteps.array,
        howToImplement: response.record.howToImplement || '',
        durationYears: response.record.durationYears || '',
        referrals: referrals.array,
        otherReferral: referrals.otherValue,
        otherAids: otherAids.array,
        otherAid: otherAids.otherValue,
        provincialCouncilAssistance: provincialCouncil.array,
        yearlyProgress: Object.fromEntries(
          Object.entries(response.record.yearlyProgress || {}).map(([year, progress]) => {
            const aidsResult = parseArrayWithOther(progress.aids || '');
            const needsResult = parseArrayWithOther(progress.needs || '');
            return [year, {
              aids: aidsResult.array,
              aidOther: aidsResult.otherValue,
              needs: needsResult.array,
              needsOther: needsResult.otherValue,
            }];
          })
        ),
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setFormData((prev) => {
      const currentArray = prev[fieldName];
      const newArray = checked
        ? [...currentArray, value]
        : currentArray.filter((item) => item !== value);

      const updates = { [fieldName]: newArray };

      // Clear "other" fields
      if (!checked && value === 'Other') {
        if (fieldName === 'referrals') updates.otherReferral = '';
        if (fieldName === 'otherAids') updates.otherAid = '';
      }

      // Clear provincial council if not selected
      if (
        fieldName === 'otherAids' &&
        !newArray.includes('Provincial Council Assistance')
      ) {
        updates.provincialCouncilAssistance = [];
      }

      return { ...prev, ...updates };
    });
  };

  const handleYearlyProgressChange = (year, field, value, checked = null) => {
    setFormData((prev) => {
      const raw = prev.yearlyProgress[year] || {};
      const yearProgress = {
        aids: Array.isArray(raw.aids) ? raw.aids : [],
        aidOther: raw.aidOther || '',
        needs: Array.isArray(raw.needs) ? raw.needs : [],
        needsOther: raw.needsOther || '',
      };

      if (field === 'aidOther') {
        yearProgress.aidOther = value;
      } else if (field === 'needsOther') {
        yearProgress.needsOther = value;
      } else if (checked !== null) {
        if (field === 'aids') {
          yearProgress.aids = checked
            ? [...yearProgress.aids, value]
            : yearProgress.aids.filter((item) => item !== value);
          if (!checked && value === 'Other') {
            yearProgress.aidOther = '';
          }
        } else if (field === 'needs') {
          yearProgress.needs = checked
            ? [...yearProgress.needs, value]
            : yearProgress.needs.filter((item) => item !== value);
          if (!checked && value === 'Other') {
            yearProgress.needsOther = '';
          }
        }
      }

      return {
        ...prev,
        yearlyProgress: {
          ...prev.yearlyProgress,
          [year]: yearProgress,
        },
      };
    });
  };

  const shouldShowOtherReferral = () => formData.referrals.includes('Other');
  const shouldShowOtherAid = () => formData.otherAids.includes('Other');
  const shouldShowProvincialCouncil = () =>
    formData.otherAids.includes('Provincial Council Assistance');
  const shouldShowOtherProgressAid = (year) =>
    formData.yearlyProgress[year]?.aids?.includes('Other');

  const shouldShowOtherProgressNeed = (year) =>
    formData.yearlyProgress[year]?.needs?.includes('Other');

  const hasChanges = () => {
    const arrayToString = (arr) => arr.sort().join(', ');

    // Compare basic fields
    const current = {
      ...formData,
      identifiedNeeds: arrayToString(formData.identifiedNeeds),
      interventions: arrayToString(formData.interventions),
      proposedSteps: arrayToString(formData.proposedSteps),
      referrals: arrayToString(formData.referrals),
      otherAids: arrayToString(formData.otherAids),
      provincialCouncilAssistance: arrayToString(
        formData.provincialCouncilAssistance
      ),
      yearlyProgress: JSON.stringify(formData.yearlyProgress),
    };

    const original = {
      ...originalData,
      identifiedNeeds: arrayToString(originalData.identifiedNeeds),
      interventions: arrayToString(originalData.interventions),
      proposedSteps: arrayToString(originalData.proposedSteps),
      referrals: arrayToString(originalData.referrals),
      otherAids: arrayToString(originalData.otherAids),
      provincialCouncilAssistance: arrayToString(
        originalData.provincialCouncilAssistance
      ),
      yearlyProgress: JSON.stringify(originalData.yearlyProgress),
    };

    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const arrayToStringWithOther = (arr, otherValue) => {
    const filtered = arr.filter((item) => item !== 'Other');
    if (arr.includes('Other') && otherValue) {
      filtered.push(`Other: ${otherValue}`);
    }
    return filtered.join(', ');
  };

  const handleSave = async () => {
    // Validation
    if (
      !formData.identifiedNeeds.length ||
      !formData.interventions.length ||
      !formData.proposedSteps.length ||
      !formData.howToImplement ||
      !formData.durationYears
    ) {
      setAlert({
        show: true,
        message: 'Please fill in all required fields.',
        type: 'warning',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      // Build yearly progress data with "Other:" formatting
      const yearlyProgressFormatted = {};
      const yearsWithData = [];

      Object.keys(formData.yearlyProgress).forEach((year) => {
        const progress = formData.yearlyProgress[year];
        if (progress.aids?.length || progress.needs?.length) {
          const aids = arrayToStringWithOther(
            progress.aids || [],
            progress.aidOther || ''
          );
          const needs = arrayToStringWithOther(
            progress.needs || [],
            progress.needsOther || ''
          );

          if (aids || needs) {
            yearlyProgressFormatted[year] = { aids, needs };
            yearsWithData.push(year);
          }
        }
      });

      const updateData = {
        uniqueId: recordData.uniqueId,
        identifiedNeeds: formData.identifiedNeeds.join(', '),
        interventions: formData.interventions.join(', '),
        proposedSteps: formData.proposedSteps.join(', '),
        howToImplement: formData.howToImplement,
        durationYears: formData.durationYears,
        referrals: arrayToStringWithOther(
          formData.referrals,
          formData.otherReferral
        ),
        otherAids: arrayToStringWithOther(
          formData.otherAids,
          formData.otherAid
        ),
        provincialCouncilAssistance:
          formData.provincialCouncilAssistance.join(', '),
        progressYears: yearsWithData.join(', '),
        yearlyProgress: yearlyProgressFormatted,
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateRehabilitationPlanDetails(
        updateData
      );

      if (response.success) {
        setAlert({
          show: true,
          message: 'Rehabilitation plan details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBackToSearch = () => {
    navigate('/search');
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={12}
        title="Update Rehabilitation Plan Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading rehabilitation plan details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={12}
        title="Update Rehabilitation Plan Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load rehabilitation plan details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={12}
      title="Update Rehabilitation Plan Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={null}
      onPrevious={() =>
        navigate(`/update/welfare/${recordId}?sheetId=${sheetId}`)
      }
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
      showBackToSearch={true}
      onBackToSearch={handleBackToSearch}
      showNext={false}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">
          Rehabilitation Plan Details (Editable)
        </div>

        <Form.Group className="mb-3">
          <Form.Label>
            Identified Need as per the Manual (Select all that apply){' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <div className="checkbox-group">
            {legendData?.identifiedNeeds?.map((item, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                id={`need-${index}`}
                label={item}
                checked={formData.identifiedNeeds.includes(item)}
                onChange={(e) =>
                  handleCheckboxChange(
                    'identifiedNeeds',
                    item,
                    e.target.checked
                  )
                }
                disabled={!canEdit}
              />
            ))}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Intervention as per the Manual (Select all that apply){' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <div className="checkbox-group">
            {legendData?.interventions?.map((item, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                id={`intervention-${index}`}
                label={item}
                checked={formData.interventions.includes(item)}
                onChange={(e) =>
                  handleCheckboxChange('interventions', item, e.target.checked)
                }
                disabled={!canEdit}
              />
            ))}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Proposed Steps to Implement Identified Interventions (Select all
            that apply) <span className="text-danger">*</span>
          </Form.Label>
          <div className="checkbox-group">
            {legendData?.proposedSteps?.map((item, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                id={`step-${index}`}
                label={item}
                checked={formData.proposedSteps.includes(item)}
                onChange={(e) =>
                  handleCheckboxChange('proposedSteps', item, e.target.checked)
                }
                disabled={!canEdit}
              />
            ))}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            How to do it? <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            name="howToImplement"
            value={formData.howToImplement}
            onChange={handleInputChange}
            placeholder="Describe how to implement the rehabilitation plan..."
            required
            disabled={!canEdit}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Duration (Years) of Rehabilitation Plan{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="number"
            name="durationYears"
            value={formData.durationYears}
            onChange={handleInputChange}
            placeholder="Enter number of years"
            min="1"
            required
            disabled={!canEdit}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Referrals of Rehabilitation Plan (Select all that apply)
          </Form.Label>
          <div className="checkbox-group">
            {legendData?.referrals?.map((item, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                id={`referral-${index}`}
                label={item}
                checked={formData.referrals.includes(item)}
                onChange={(e) =>
                  handleCheckboxChange('referrals', item, e.target.checked)
                }
                disabled={!canEdit}
              />
            ))}
          </div>
        </Form.Group>

        {shouldShowOtherReferral() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Please specify other referral</Form.Label>
            <Form.Control
              type="text"
              name="otherReferral"
              value={formData.otherReferral}
              onChange={handleInputChange}
              placeholder="Enter other referral"
              disabled={!canEdit}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            Other Aids for Rehabilitation Plan (Select all that apply)
          </Form.Label>
          <div className="checkbox-group">
            {legendData?.otherAids?.map((item, index) => (
              <Form.Check
                key={index}
                type="checkbox"
                id={`aid-${index}`}
                label={item}
                checked={formData.otherAids.includes(item)}
                onChange={(e) =>
                  handleCheckboxChange('otherAids', item, e.target.checked)
                }
                disabled={!canEdit}
              />
            ))}
          </div>
        </Form.Group>

        {shouldShowOtherAid() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Please specify other aid</Form.Label>
            <Form.Control
              type="text"
              name="otherAid"
              value={formData.otherAid}
              onChange={handleInputChange}
              placeholder="Enter other aid"
              disabled={!canEdit}
            />
          </Form.Group>
        )}

        {shouldShowProvincialCouncil() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>
              Received Assistance from Provincial Council (Select all that
              apply)
            </Form.Label>
            <div className="checkbox-group">
              {legendData?.provincialCouncilAssistance?.map((item, index) => (
                <Form.Check
                  key={index}
                  type="checkbox"
                  id={`provincial-${index}`}
                  label={item}
                  checked={formData.provincialCouncilAssistance.includes(item)}
                  onChange={(e) =>
                    handleCheckboxChange(
                      'provincialCouncilAssistance',
                      item,
                      e.target.checked
                    )
                  }
                  disabled={!canEdit}
                />
              ))}
            </div>
            <span className="info-badge">
              Shown when Provincial Council Assistance is selected
            </span>
          </Form.Group>
        )}

        {/* YEARLY PROGRESS SECTIONS */}
        <div className="section-header">Progress of Rehabilitation Plan</div>

        {legendData?.progressYears?.length > 0 ? (
          legendData.progressYears.map((year) => {
            const yearProgress = formData.yearlyProgress[year] || {
              aids: [],
              aidOther: '',
              needs: [],
            };

            return (
              <div
                key={year}
                className="year-section"
                style={{
                  marginBottom: '2rem',
                  padding: '1.5rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                }}
              >
                <h5 style={{ color: '#667eea', marginBottom: '1rem' }}>
                  Progress for {year}
                </h5>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Progress of Aids ({year}) - Select all that apply
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.otherAids?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`progressAid-${year}-${index}`}
                        label={item}
                        checked={yearProgress.aids?.includes(item)}
                        onChange={(e) =>
                          handleYearlyProgressChange(
                            year,
                            'aids',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                </Form.Group>

                {shouldShowOtherProgressAid(year) && (
                  <Form.Group className="mb-3 conditional-field">
                    <Form.Label>
                      Please specify other progress aid for {year}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={yearProgress.aidOther || ''}
                      onChange={(e) =>
                        handleYearlyProgressChange(
                          year,
                          'aidOther',
                          e.target.value
                        )
                      }
                      placeholder="Enter other progress aid"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>
                    Progress of Referrals ({year}) - Select all that apply
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.referrals?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`progressNeed-${year}-${index}`}
                        label={item}
                        checked={yearProgress.needs?.includes(item)}
                        onChange={(e) =>
                          handleYearlyProgressChange(
                            year,
                            'needs',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                </Form.Group>

                {shouldShowOtherProgressNeed(year) && (
                  <Form.Group className="mb-3 conditional-field">
                    <Form.Label>
                      Please specify other progress referral for {year}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={yearProgress.needsOther || ''}
                      onChange={(e) =>
                        handleYearlyProgressChange(
                          year,
                          'needsOther',
                          e.target.value
                        )
                      }
                      placeholder="Enter other referral"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-muted">No progress years available.</p>
        )}
      </Form>
    </UpdateLayout>
  );
}

export default UpdateRehabilitation;
